import React, { useState } from "react";
import api from "../../services/api";
import ButtonWrapDiv from "../../components/common/ButtonWrapDiv";
import FunctionButton from "../../components/common/FunctionButton";
import InputWrapDiv from "../../components/common/InputWrapDiv";
import TopBoxDiv from "../../components/common/TopBoxDiv";
import ContentInput from "../../components/common/ContentInput";
import { useTranslation } from "react-i18next";

const UnsupportCarManagementEditModal = ({
  editTarget,
  canEdit,
  onSaved,
  onClose,
}) => {
  const { t } = useTranslation();

  const isEdit = editTarget !== null && editTarget !== undefined;

  const [carModel, setCarModel] = useState(editTarget?.carModel || "");
  const [carYear, setCarYear] = useState(editTarget?.carYear || "");
  const [appStatus, setAppStatus] = useState(editTarget?.appStatus || "ALERT");

  const appStatusOptions = ["PASS", "ALERT", "BLOCK"];

  // 모델 코드 중복확인 상태 (수정 시 기존 값은 확인된 것으로 간주)
  const [modelChecked, setModelChecked] = useState(isEdit);
  const [showAvailMsg, setShowAvailMsg] = useState(false);

  const [errors, setErrors] = useState({
    validCarModel: "",
    validCarYear: "",
  });

  const handleCarModelChange = (e) => {
    setCarModel(e.target.value);
    // 값이 바뀌면 이전 중복확인 결과 무효화
    setModelChecked(false);
    setShowAvailMsg(false);
    setErrors((prev) => ({ ...prev, validCarModel: "" }));
  };

  const handleCheck = () => {
    if (!carModel.trim()) {
      setShowAvailMsg(false);
      setErrors((prev) => ({ ...prev, validCarModel: "Car Model is required." }));
      return;
    }

    api
      .post("/manage/unsupportcar/check", {
        carModel: carModel.trim(),
        ...(isEdit ? { id: editTarget.id } : {}),
      })
      .then((response) => {
        if (response.data === 0) {
          setModelChecked(true);
          setShowAvailMsg(true);
          setErrors((prev) => ({ ...prev, validCarModel: "" }));
        } else {
          setModelChecked(false);
          setShowAvailMsg(false);
          setErrors((prev) => ({ ...prev, validCarModel: t("L0124") }));
        }
      })
      .catch(() => {});
  };

  const validate = () => {
    let tempErrors = {};
    let isValid = true;

    if (!carModel.trim()) {
      tempErrors.validCarModel = "Car Model is required.";
      isValid = false;
    } else if (!modelChecked) {
      // 중복확인 미완료
      tempErrors.validCarModel = t("L1095");
      isValid = false;
    }

    if (!carYear) {
      tempErrors.validCarYear = "Model Year is required.";
      isValid = false;
    } else if (!/^(ALL|\d{4})$/.test(carYear.toUpperCase())) {
      tempErrors.validCarYear = "Model Year must be 'ALL' or a 4-digit year.";
      isValid = false;
    }

    setErrors(tempErrors);
    if (!isValid) {
      setShowAvailMsg(false);
    }
    return isValid;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    const payload = {
      carModel: carModel.trim(),
      carYear: carYear.trim().toUpperCase(),
      appStatus: appStatus,
    };

    const request = isEdit
      ? api.post("/manage/unsupportcar/update", {
          id: editTarget.id,
          ...payload,
        })
      : api.post("/manage/unsupportcar/add", payload);

    request
      .then(() => {
        onSaved();
      })
      .catch((error) => {
        if (error?.response?.data?.result === 1) {
          setShowAvailMsg(false);
          setModelChecked(false);
          setErrors({ validCarModel: t("L0124") });
        }
      });
  };

  return (
    <div>
      <InputWrapDiv>
        <TopBoxDiv value={t("MENU:L1092")}></TopBoxDiv>

        <table>
          <tbody>
            <tr>
              <th>
                {t("L0672")} *
                {errors.validCarModel && (
                  <>
                    <br />
                    <span style={{ color: "red", fontSize: "10px" }}>
                      {errors.validCarModel}
                    </span>
                  </>
                )}
                {!errors.validCarModel && showAvailMsg && (
                  <>
                    <br />
                    <span style={{ color: "#0067a3", fontSize: "10px" }}>
                      {t("L1094")}
                    </span>
                  </>
                )}
              </th>
              <td>
                <div style={{ display: "flex", gap: "4px" }}>
                  <ContentInput
                    value={carModel}
                    maxLength={100}
                    width="70%"
                    onChange={handleCarModelChange}
                    disabled={!canEdit}
                  ></ContentInput>
                  <FunctionButton
                    className="small"
                    value={t("L0359")}
                    onClick={handleCheck}
                  ></FunctionButton>
                </div>
              </td>
            </tr>
            <tr>
              <th>
                {t("L1093")} *
                {errors.validCarYear && (
                  <>
                    <br />
                    <span style={{ color: "red", fontSize: "10px" }}>
                      {errors.validCarYear}
                    </span>
                  </>
                )}
              </th>
              <td>
                <ContentInput
                  value={carYear}
                  maxLength={10}
                  width="70%"
                  placeholder={"ALL / 2023"}
                  onChange={(e) => setCarYear(e.target.value)}
                  disabled={!canEdit}
                ></ContentInput>
              </td>
            </tr>
            <tr>
              <th>{t("L1143")} *</th>
              <td>
                <div style={{ display: "flex", gap: "20px" }}>
                  {appStatusOptions.map((status) => (
                    <span
                      key={status}
                      style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                    >
                      <input
                        type="radio"
                        name="app-status"
                        id={`app-status-${status}`}
                        value={status}
                        checked={appStatus === status}
                        onChange={(e) => setAppStatus(e.target.value)}
                        disabled={!canEdit}
                      />
                      <label htmlFor={`app-status-${status}`}></label>
                      <label
                        htmlFor={`app-status-${status}`}
                        style={{ cursor: canEdit ? "pointer" : "default" }}
                      >
                        {status}
                      </label>
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </InputWrapDiv>

      <ButtonWrapDiv className="btn_bottom" style={{ margin: "16px 0" }}>
        {canEdit && (
          <FunctionButton
            className="big"
            value={t("L0659")}
            onClick={handleSubmit}
          ></FunctionButton>
        )}
        <FunctionButton
          className="big bg_gray"
          value={t("L0005")}
          onClick={onClose}
        ></FunctionButton>
      </ButtonWrapDiv>
    </div>
  );
};

export default UnsupportCarManagementEditModal;
